import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { useNavigate } from "react-router";
import { CircularProgress } from "@mui/material";
import { getApiErrorMessage } from "../../../utils/commonFunction";
import {
  checkOrderDeletion,
  softDeleteOrder,
} from "../_redux/order/orderCrud";

/**
 * Super-admin "Delete order" confirmation.
 *
 * Checks first whether anything is still running on the order (the backend
 * refuses while a Stripe subscription can bill, a container is assigned or
 * gate access is live) and lists it, so nobody types a reason for nothing.
 * Otherwise asks for a reason, deletes, and returns to the Orders list —
 * a deleted order's page can no longer be opened.
 *
 * @param {Object} props
 * @param {boolean} props.show
 * @param {() => void} props.onHide
 * @param {Object} props.orderDetail The order (id, booking_reference)
 * @returns {JSX.Element}
 */
export default function DeleteOrderModal({ show, onHide, orderDetail }) {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);
  // True only once the blocker check has come back successfully. A failed
  // check must not read as "nothing blocks this".
  const [checkComplete, setCheckComplete] = useState(false);
  const [refusals, setRefusals] = useState([]);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);

  /** Reset the form and ask the backend what, if anything, blocks deletion. */
  const handleShow = () => {
    setCheckComplete(false);
    setReason("");
    setError("");
    setRefusals([]);
    if (!orderDetail?.id) {
      setError("This order could not be checked for deletion.");
      return;
    }
    setChecking(true);
    checkOrderDeletion(orderDetail.id)
      .then((res) => {
        setRefusals(res?.data?.data?.refusals || []);
        setCheckComplete(true);
      })
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setChecking(false));
  };

  /** Delete the order with the typed reason, then go to the Orders list. */
  const handleDelete = () => {
    if (reason.trim().length < 5) {
      setError("Please give a reason (at least 5 characters).");
      return;
    }
    setDeleting(true);
    setError("");
    softDeleteOrder(orderDetail.id, reason.trim())
      .then(() => {
        onHide();
        navigate("/orders");
      })
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setDeleting(false));
  };

  const blocked = refusals.length > 0;
  const canDelete = checkComplete && !blocked;

  return (
    <Modal show={show} onShow={handleShow} onHide={deleting ? undefined : onHide} centered>
      <Modal.Header closeButton={!deleting}>
        <Modal.Title>Delete order #{orderDetail?.booking_reference}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {checking && (
          <div className="text-center py-3">
            <CircularProgress size={28} />
          </div>
        )}

        {!checking && blocked && (
          <>
            <p className="font-weight-bold mb-2">This order cannot be deleted yet:</p>
            <ul className="mb-0">
              {refusals.map((text) => (
                <li key={text}>{text}</li>
              ))}
            </ul>
          </>
        )}

        {!checking && canDelete && (
          <>
            <p>
              The order will be removed from every list and search in VAULT and
              its page will no longer open. Nothing is cancelled or refunded.
              Your name and the reason are recorded on the order.
            </p>
            <label htmlFor="delete-order-reason" className="font-weight-bold">
              Reason (required)
            </label>
            <textarea
              id="delete-order-reason"
              className="form-control"
              rows={3}
              maxLength={500}
              placeholder="e.g. Duplicate of order 058-030926-01"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={deleting}
            />
          </>
        )}

        {error && (
          <div className="mt-3" style={{ color: "#D32F2F" }}>
            {error}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-light" onClick={onHide} disabled={deleting}>
          {canDelete ? "Cancel" : "Close"}
        </button>
        {canDelete && (
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleDelete}
            disabled={checking || deleting}
          >
            {deleting ? "Deleting..." : "Delete order"}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
